import type {
  Stats,
  Arc,
  Ember,
  FlowData,
  ThreatLevel,
  GlobeInstance,
  StateSubscriber,
} from '@/types';
import { CONFIG } from '@/config';

/** Application state interface */
export interface AppState {
  // Globe instance
  globe: GlobeInstance | null;

  // WebSocket
  websocket: WebSocket | null;

  // Visualization data
  arcs: Arc[];
  embers: Ember[];
  arcWriteIndex: number;
  emberWriteIndex: number;
  arcsDirty: boolean;
  embersDirty: boolean;

  // Flow queue
  flowQueue: FlowData[];
  flowQueueHead: number;

  // Statistics
  stats: Stats;

  // UI state
  enabledThreatLevels: Set<ThreatLevel>;
  isPaused: boolean;
  savedAutoRotate: boolean;
  eventFeedAutoScroll: boolean;

  // Sparkline data
  flowsSparklineData: number[];
  bandwidthSparklineData: number[];
}

/** Create initial statistics */
function createInitialStats(): Stats {
  return {
    totalFlows: 0,
    currentBandwidth: 0,
    fps: 0,
    lastFrameTime: performance.now(),
    frameCount: 0,
    flowsThisSecond: 0,
    flowRate: 0,
    lastFlowRateUpdate: performance.now(),
    threats: 0,
  };
}

/** Create initial application state */
function createInitialState(): AppState {
  return {
    globe: null,
    websocket: null,
    arcs: [],
    embers: [],
    arcWriteIndex: 0,
    emberWriteIndex: 0,
    arcsDirty: false,
    embersDirty: false,
    flowQueue: [],
    flowQueueHead: 0,
    stats: createInitialStats(),
    enabledThreatLevels: new Set([0, 1, 2, 3, 4, 5] as ThreatLevel[]),
    isPaused: false,
    savedAutoRotate: true,
    eventFeedAutoScroll: true,
    flowsSparklineData: new Array(CONFIG.sparklinePoints).fill(0),
    bandwidthSparklineData: new Array(CONFIG.sparklinePoints).fill(0),
  };
}

/**
 * Centralized state management store
 * Provides a single source of truth for application state
 */
class StateStore {
  private state: AppState;
  private subscribers: Map<keyof AppState | '*', Set<StateSubscriber<AppState>>>;

  constructor() {
    this.state = createInitialState();
    this.subscribers = new Map();
  }

  /** Get the current state (read-only snapshot) */
  getState(): Readonly<AppState> {
    return this.state;
  }

  /** Get a specific state property */
  get<K extends keyof AppState>(key: K): AppState[K] {
    return this.state[key];
  }

  /** Set a specific state property */
  set<K extends keyof AppState>(key: K, value: AppState[K]): void {
    this.state[key] = value;
    this.notify(key);
  }

  /** Update multiple state properties at once */
  update(updates: Partial<AppState>): void {
    const changedKeys: (keyof AppState)[] = [];

    for (const key of Object.keys(updates) as (keyof AppState)[]) {
      if (updates[key] !== undefined) {
        (this.state as Record<keyof AppState, unknown>)[key] = updates[key];
        changedKeys.push(key);
      }
    }

    // Notify subscribers for each changed key
    for (const key of changedKeys) {
      this.notify(key);
    }
  }

  /** Subscribe to state changes */
  subscribe(
    key: keyof AppState | '*',
    callback: StateSubscriber<AppState>
  ): () => void {
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, new Set());
    }
    this.subscribers.get(key)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.subscribers.get(key)?.delete(callback);
    };
  }

  /** Notify subscribers of a state change */
  private notify(key: keyof AppState): void {
    // Notify specific key subscribers
    this.subscribers.get(key)?.forEach((callback) => callback(this.state));
    // Notify wildcard subscribers
    this.subscribers.get('*')?.forEach((callback) => callback(this.state));
  }

  /** Reset state to initial values */
  reset(): void {
    this.state = createInitialState();
    this.notify('stats');
  }

  // ==================== Convenience Methods ====================

  /** Add a flow to the queue */
  queueFlow(flow: FlowData): void {
    this.state.flowQueue.push(flow);
  }

  /** Get and increment the arc write index */
  nextArcIndex(): number {
    const index = this.state.arcWriteIndex;
    this.state.arcWriteIndex = (index + 1) % CONFIG.maxArcs;
    return index;
  }

  /** Get and increment the ember write index */
  nextEmberIndex(): number {
    const index = this.state.emberWriteIndex;
    this.state.emberWriteIndex = (index + 1) % CONFIG.maxEmbers;
    return index;
  }

  /** Increment total flows count */
  incrementFlows(): void {
    this.state.stats.totalFlows++;
    this.state.stats.flowsThisSecond++;
  }

  /** Increment threats count */
  incrementThreats(): void {
    this.state.stats.threats++;
  }

  /** Update FPS counter */
  updateFPS(): boolean {
    const now = performance.now();
    this.state.stats.frameCount++;

    if (now - this.state.stats.lastFrameTime >= 1000) {
      this.state.stats.fps = this.state.stats.frameCount;
      this.state.stats.frameCount = 0;
      this.state.stats.lastFrameTime = now;
      return true; // Indicates a second has passed
    }
    return false;
  }

  /** Update flow rate */
  updateFlowRate(): void {
    const now = performance.now();
    if (now - this.state.stats.lastFlowRateUpdate >= 1000) {
      this.state.stats.flowRate = this.state.stats.flowsThisSecond;
      this.state.stats.flowsThisSecond = 0;
      this.state.stats.lastFlowRateUpdate = now;
    }
  }

  /** Toggle a threat level filter */
  toggleThreatLevel(level: ThreatLevel): boolean {
    const enabled = this.state.enabledThreatLevels;
    if (enabled.has(level)) {
      enabled.delete(level);
      return false;
    } else {
      enabled.add(level);
      return true;
    }
  }

  /** Check if a threat level is enabled */
  isThreatLevelEnabled(level: ThreatLevel): boolean {
    return this.state.enabledThreatLevels.has(level);
  }

  /** Clear all flows and reset visualization */
  clearFlows(): void {
    this.state.arcs = [];
    this.state.embers = [];
    this.state.arcWriteIndex = 0;
    this.state.emberWriteIndex = 0;
    this.state.flowQueue.length = 0;
    this.state.flowQueueHead = 0;
    this.state.stats.totalFlows = 0;
    this.state.stats.currentBandwidth = 0;
    this.state.stats.flowsThisSecond = 0;
    this.state.stats.flowRate = 0;
    this.state.stats.threats = 0;

    // Update globe if available
    const globe = this.state.globe;
    if (globe) {
      globe.arcsData([]);
      globe.objectsData([]);
    }

    this.notify('stats');
  }

  /** Push data to sparkline arrays */
  pushSparklineData(): void {
    // Shift arrays and push new values
    this.state.flowsSparklineData.shift();
    this.state.flowsSparklineData.push(this.state.stats.flowRate);

    this.state.bandwidthSparklineData.shift();
    this.state.bandwidthSparklineData.push(this.state.stats.currentBandwidth);
  }
}

/** Global state store instance */
export const store = new StateStore();
